import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

export default function Header({ t, lang, setLang, logo, contact }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: t.nav.services },
    { href: "#why", label: t.nav.why },
    { href: "#coverage", label: t.nav.coverage },
    { href: "#reviews", label: t.nav.reviews },
    { href: "#faq", label: t.nav.faq },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      className={`sticky top-0 z-50 border-b-2 border-black transition-colors ${
        scrolled ? "bg-white/95 backdrop-blur" : "bg-white"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
          <img src={logo} alt="Raskt Hjem AS" className="h-10 md:h-12 w-auto" />
          <div className="hidden md:flex flex-col leading-none">
            <span className="font-display text-lg tracking-wide">RASKT HJEM AS</span>
            <span className="text-[10px] tracking-[0.2em] text-[#E11D2E] font-semibold">
              STAVANGER · ROGALAND
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium hover:text-[#E11D2E] transition-colors"
              data-testid={`nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex border border-black overflow-hidden text-xs font-bold">
            <button
              onClick={() => setLang("no")}
              className={`px-2.5 py-1 ${lang === "no" ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}
              data-testid="lang-toggle-no"
            >
              NO
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 border-l border-black ${lang === "en" ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}
              data-testid="lang-toggle-en"
            >
              EN
            </button>
          </div>

          <a
            href={`tel:${contact.phone_tel}`}
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-[#E11D2E]"
            data-testid="header-phone-link"
          >
            <Phone size={16} /> {contact.phone_display}
          </a>

          <a
            href="#quote"
            className="hidden sm:inline-block bg-[#E11D2E] text-white px-4 md:px-5 py-2.5 md:py-3 font-bold text-xs md:text-sm tracking-wider uppercase border-2 border-black brutal-shadow"
            data-testid="header-cta-quote"
          >
            {t.nav.quote}
          </a>

          <button
            className="lg:hidden ml-1 p-2 border-2 border-black"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-black bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base font-medium border-b border-zinc-200"
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setLang("no")}
                className={`flex-1 py-2 border-2 border-black text-sm font-bold ${lang === "no" ? "bg-black text-white" : "bg-white"}`}
                data-testid="mobile-lang-no"
              >
                NORSK
              </button>
              <button
                onClick={() => setLang("en")}
                className={`flex-1 py-2 border-2 border-black text-sm font-bold ${lang === "en" ? "bg-black text-white" : "bg-white"}`}
                data-testid="mobile-lang-en"
              >
                ENGLISH
              </button>
            </div>
            <a
              href={`tel:${contact.phone_tel}`}
              className="py-3 text-center border-2 border-black font-bold flex items-center justify-center gap-2"
              data-testid="mobile-phone-link"
            >
              <Phone size={16} /> {contact.phone_display}
            </a>
            <a
              href="#quote"
              onClick={() => setOpen(false)}
              className="py-3 text-center bg-[#E11D2E] text-white border-2 border-black font-bold uppercase tracking-wider"
              data-testid="mobile-cta-quote"
            >
              {t.nav.quote}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
